import { getEntitlements } from './billingEntitlements';

export const EMPTY_BILLING_STATUS = {
  billingState: 'not_provisioned',
  accessState: 'not_provisioned',
  billingOwner: 'none',
  plan: null,
  entitlementState: 'none',
  trialEndsAt: null,
  trialDaysRemaining: null,
  currentPeriodEnd: null,
  currentPeriodEndsAt: null,
  cancelAtPeriodEnd: false,
  webPaymentMethodAttached: false,
  hasActiveTrial: false,
  hasActivePaidEntitlement: false,
  canStartAppleTrial: false,
  canPurchaseOnIOS: false,
  purchaseSuppressionReason: 'none',
  billingReadOnlyReason: null,
  showRestorePurchases: false,
  manageBillingMode: 'none',
  showNativeSubscribeLock: false,
  showOnboardingBillingStep: false,
  hasPaymentMethod: false,
  paymentMethodBrand: null,
  paymentMethodLast4: null,
  pastDueSince: null,
  lastPaymentFailedAt: null,
  gracePeriodEndsAt: null,
  daysUntilAccessRestricted: null,
  billingDesignation: 'standard',
  billingBypassed: false,
  viewerRole: null,
  billingOwnerUserId: null,
  billingOwnerName: null,
  billingOwnerUser: null,
  canManageBilling: true,
};

export const BILLING_UNAVAILABLE_STATUS = {
  ...EMPTY_BILLING_STATUS,
  billingState: 'billing_unavailable',
  accessState: 'billing_unavailable',
};

export const normalizeBillingStatus = (status) => {
  const normalized = { ...EMPTY_BILLING_STATUS, ...(status || {}) };
  if (!normalized.currentPeriodEnd && normalized.currentPeriodEndsAt) {
    normalized.currentPeriodEnd = normalized.currentPeriodEndsAt;
  }
  if (!normalized.billingOwnerName && normalized.billingOwnerUser?.firstName) {
    normalized.billingOwnerName = normalized.billingOwnerUser.firstName;
  }
  if (normalized.billingReadOnlyReason === 'managed_by_owner') {
    normalized.canManageBilling = false;
  }
  normalized.hasPaymentMethod = Boolean(
    normalized.hasPaymentMethod ||
    normalized.webPaymentMethodAttached ||
    normalized.paymentMethodLast4
  );
  if (normalized.billingState === 'trialing_with_payment_method' && !normalized.hasPaymentMethod) {
    normalized.billingState = 'trialing_no_payment_method';
  }
  return normalized;
};

export const isAppleManagedBilling = (status) => {
  const normalized = normalizeBillingStatus(status);
  return Boolean(
    !normalized.billingBypassed &&
    (
      normalized.billingOwner === 'apple' ||
      normalized.billingReadOnlyReason === 'managed_in_app_store' ||
      normalized.manageBillingMode === 'app_store'
    )
  );
};

export const isManagedByOwnerBilling = (status) => {
  const normalized = normalizeBillingStatus(status);
  return Boolean(
    !normalized.billingBypassed &&
    (
      normalized.canManageBilling === false ||
      normalized.billingReadOnlyReason === 'managed_by_owner' ||
      normalized.purchaseSuppressionReason === 'non_owner'
    )
  );
};

export const canUseStripeBillingControls = (status) => {
  const normalized = normalizeBillingStatus(status);
  if (normalized.billingBypassed) return false;
  if (isManagedByOwnerBilling(normalized)) return false;
  if (isAppleManagedBilling(normalized)) return false;
  return normalized.billingOwner === 'web' || normalized.billingOwner === 'none';
};

export const webBillingReadOnlyReason = (status) => {
  const normalized = normalizeBillingStatus(status);
  if (isManagedByOwnerBilling(normalized)) return 'managed_by_owner';
  if (isAppleManagedBilling(normalized)) return 'managed_in_app_store';
  return null;
};

export const shouldRevalidateBillingStatus = (status) => {
  const entitlements = getEntitlements(normalizeBillingStatus(status).accessState);
  return entitlements.isBlocked || !entitlements.isProvisioned;
};

export const hasProvisionedBillingStatus = (status) => {
  const normalized = normalizeBillingStatus(status);
  return (
    (normalized.billingState && normalized.billingState !== 'not_provisioned') ||
    (normalized.accessState && normalized.accessState !== 'not_provisioned')
  );
};
